/**
 * D6 - cycle review, then the consolidated invoice (FRD §4 step 4).
 *
 * THREE STEPS, DELIBERATELY. Review what is about to be billed → confirm →
 * the generated document. Generation closes the cycle and cannot be undone, so
 * the review step exists to stop an accidental invoice; the design brief calls
 * it out in those words and this page keeps them.
 *
 * THE DOCUMENT IS NOT BUILT HERE. Generation is a server transaction (close the
 * cycle, mint the number, open the next) and the PDF comes from
 * backend/packages/invoice. Nothing on this page draws invoice markup - see
 * docs/INVOICE_DESIGN.md and scripts/check-invoice-boundary.js.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Download, FileText, Loader2, Printer } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useColors } from '../../hooks/useColors'
import EmptyState from '../../components/shared/EmptyState'
import LoadErrorState from '../../components/common/LoadErrorState'
import { withServerTimeout } from '../../components/common/loadErrorUtils'
import {
  Card,
  CardTitle,
  Chip,
  Modal,
  PageHeader,
  PillButton,
  SearchInput,
  StatCard,
  StatRow,
  TableHead,
  Td,
} from '../../features/b2b/components/primitives'
import { rowsFromCycle } from '../../features/b2b/components/billingRowModel'
import { useInvoiceDocument } from '../../features/b2b/useInvoiceDocument'
import { formatMoney } from '../../features/b2b/format'
import { b2bErrorMessage } from '../../features/b2b/errorCopy'
import { b2bApi } from '../../services/b2bApi'
import type { B2bCycleResponse, B2bInvoiceRecord } from '../../services/b2bApi'

/** Where the operator is in the three-step flow. */
type Stage = 'review' | 'generating' | 'done'

export default function B2bGenerateInvoicePage() {
  const colors = useColors()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const clientId = searchParams.get('client')

  const [cycle, setCycle] = useState<B2bCycleResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [showZeroRooms, setShowZeroRooms] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const [stage, setStage] = useState<Stage>('review')
  const [invoice, setInvoice] = useState<B2bInvoiceRecord | null>(null)
  /** True when the POST found the cycle already invoiced (§ the double-click). */
  const [alreadyGenerated, setAlreadyGenerated] = useState(false)
  const [generateError, setGenerateError] = useState('')

  const load = useCallback(async () => {
    if (!clientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      setCycle(await withServerTimeout(b2bApi.getOpenCycle(clientId)))
    } catch (err) {
      setError(b2bErrorMessage(err, 'loading this billing cycle'))
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    void load()
  }, [load])

  const rows = useMemo(() => (cycle ? rowsFromCycle(cycle) : []), [cycle])
  const billedRows = useMemo(() => rows.filter((row) => row.total > 0), [rows])
  const zeroRooms = useMemo(() => rows.filter((row) => row.total === 0), [rows])

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return billedRows
    return billedRows.filter((row) => row.roomNumber.toLowerCase().includes(query))
  }, [billedRows, search])

  const summary = cycle?.summary
  const grandTotal = summary?.total_amount ?? 0
  const roomsBilled = summary?.rooms_billed ?? billedRows.length

  // The document only exists once generation has succeeded.
  const document = useInvoiceDocument(stage === 'done' && invoice ? invoice.id : undefined)

  const runGeneration = useCallback(async () => {
    if (!cycle || !clientId) return
    setConfirming(false)
    setGenerateError('')
    setStage('generating')

    try {
      const result = await b2bApi.generateInvoice(clientId, cycle.cycle.id)
      setInvoice(result.invoice)
      setAlreadyGenerated(!result.created)
      setStage('done')
    } catch (err) {
      setGenerateError(b2bErrorMessage(err, 'generating the invoice'))
      setStage('review')
    }
  }, [clientId, cycle])

  /* ── guards ─────────────────────────────────────────────────────── */

  if (!clientId) {
    return (
      <div>
        <PageHeader title="Generate Client Invoice" />
        <Card section>
          <EmptyState
            title="Pick a client first"
            description="Open a client from the B2B client master to review its cycle."
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PillButton onClick={() => navigate('/b2b/clients')}>Back to Clients</PillButton>
          </div>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Generate Client Invoice" />
        <LoadErrorState message={error} onRetry={() => void load()} />
      </div>
    )
  }

  /* ── step 3: the generated invoice ──────────────────────────────── */

  if (stage === 'done' && invoice && cycle) {
    return (
      <div>
        <PageHeader
          title="Client invoice generated"
          subtitle={`${cycle.client.name} · ${cycle.cycle.label}`}
        />
        <Card section>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
              padding: '32px 20px',
              textAlign: 'center',
            }}
          >
            <CheckCircle2 size={44} color="var(--chip-success-ink)" />

            <div className="font-display" style={{ fontSize: '24px', color: colors.textPrimary }}>
              {invoice.invoice_number}
            </div>

            {alreadyGenerated ? (
              // The design's wording for a second press: this is an answer, not
              // an error - the operator got the invoice they asked for.
              <p
                className="font-body"
                data-testid="already-generated"
                style={{ margin: 0, fontSize: '14px', color: 'var(--chip-warning-ink)', fontWeight: 600, maxWidth: '520px' }}
              >
                Invoice {invoice.invoice_number} has already been generated for this billing period.
              </p>
            ) : (
              <p
                className="font-body"
                style={{ margin: 0, fontSize: '14px', color: colors.textSecondary, maxWidth: '520px', lineHeight: 1.6 }}
              >
                One consolidated document for {cycle.client.name}, covering the{' '}
                {cycle.cycle.label} billing period.
              </p>
            )}

            <StatRow>
              <StatCard label="Rooms billed" value={String(invoice.rooms_billed)} />
              <StatCard label="Total payable" value={formatMoney(invoice.total_amount)} color="var(--chip-success-ink)" />
            </StatRow>

            {document.error ? (
              <p className="font-body" style={{ margin: 0, fontSize: '13px', color: 'var(--chip-danger-ink)' }}>
                {document.error}
              </p>
            ) : null}

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <PillButton
                variant="secondary"
                onClick={document.download}
                disabled={!document.url}
                title={document.url ? undefined : 'Preparing the document…'}
              >
                <Download size={15} /> Download PDF
              </PillButton>
              <PillButton
                variant="secondary"
                onClick={document.print}
                disabled={!document.url}
                title={document.url ? undefined : 'Preparing the document…'}
              >
                <Printer size={15} /> Print
              </PillButton>
              <PillButton onClick={() => navigate(`/b2b/invoices/${invoice.id}`)}>
                View invoice
              </PillButton>
              <PillButton variant="secondary" onClick={() => navigate(`/b2b/billing?client=${clientId}`)}>
                Back to B2B Billing
              </PillButton>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  /* ── step 2: generation in flight ───────────────────────────────── */

  if (stage === 'generating') {
    return (
      <div>
        <PageHeader title="Generate Client Invoice" subtitle={cycle?.client.name} />
        <Card section>
          <div
            data-testid="generating"
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '14px',
              padding: '48px 20px',
              textAlign: 'center',
            }}
          >
            <Loader2 size={36} className="rapidry-spin" color={colors.textPrimary} />
            <div className="font-display" style={{ fontSize: '19px' }}>
              Generating the invoice
            </div>
            <p
              className="font-body"
              style={{ margin: 0, fontSize: '14px', color: colors.textSecondary, maxWidth: '460px', lineHeight: 1.6 }}
            >
              Preparing one consolidated invoice for {cycle?.client.name ?? 'this client'}. Do not
              close this window.
            </p>
          </div>
        </Card>
      </div>
    )
  }

  /* ── step 1: review ─────────────────────────────────────────────── */

  return (
    <div>
      <PageHeader
        title="Generate Client Invoice"
        subtitle={cycle ? `${cycle.client.name} · ${cycle.cycle.label}` : 'Loading…'}
        actions={
          <>
            <PillButton variant="secondary" onClick={() => navigate(`/b2b/billing?client=${clientId}`)}>
              Back to Billing
            </PillButton>
            <PillButton
              onClick={() => setConfirming(true)}
              disabled={loading || roomsBilled === 0}
              title={roomsBilled === 0 ? 'Bill at least one room before invoicing' : undefined}
            >
              <FileText size={15} /> Generate Invoice
            </PillButton>
          </>
        }
      />

      {generateError ? (
        <div
          role="alert"
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            borderRadius: '10px',
            border: '1px solid rgba(180,83,9,0.35)',
            background: 'rgba(180,83,9,0.08)',
          }}
        >
          <span className="font-body" style={{ fontSize: '13px', fontWeight: 600 }}>
            {generateError}
          </span>
        </div>
      ) : null}

      <StatRow>
        <StatCard label="Rooms Billed" value={String(roomsBilled)} hint={`of ${rows.length} rooms`} />
        <StatCard label="Total Pieces" value={String(summary?.piece_count ?? 0)} />
        <StatCard label="Wash & Iron Value" value={formatMoney(summary?.by_entry_type.weight ?? 0)} />
        <StatCard label="Item Services Value" value={formatMoney(summary?.by_entry_type.items ?? 0)} />
        <StatCard label="Grand Total" value={formatMoney(grandTotal)} color="var(--chip-success-ink)" />
      </StatRow>

      <Card section>
        <CardTitle
          right={
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Find a room…"
              width="180px"
              ariaLabel="Find a room"
            />
          }
        >
          Room-wise breakup
        </CardTitle>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <TableHead
              columns={[
                { key: 'room', label: 'Room' },
                { key: 'block', label: 'Block' },
                { key: 'weight', label: 'W & I' },
                { key: 'items', label: 'Item services' },
                { key: 'pieces', label: 'Pieces', align: 'right' },
                { key: 'amount', label: 'Amount', align: 'right' },
              ]}
            />
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div style={{ padding: '16px', display: 'grid', gap: '8px' }}>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div
                          key={index}
                          className="rapidry-pulse"
                          style={{ height: '28px', borderRadius: '8px', background: colors.surface }}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyState
                      title="No rooms billed yet"
                      description="Log at least one room in the console before generating the invoice."
                    />
                  </td>
                </tr>
              ) : (
                filteredRows.map((row) => (
                  <tr key={row.roomId}>
                    <Td strong>{row.roomNumber}</Td>
                    <Td muted>{row.block ?? '-'}</Td>
                    <Td muted>{row.weightAmount > 0 ? formatMoney(row.weightAmount) : '-'}</Td>
                    <Td muted>
                      {row.dcAmount + row.shoeAmount > 0
                        ? formatMoney(row.dcAmount + row.shoeAmount)
                        : '-'}
                    </Td>
                    <Td align="right">{row.pieces ?? 0}</Td>
                    <Td align="right">
                      {/* Editing is still allowed until the invoice exists (FRD §8). */}
                      <button
                        type="button"
                        className="font-body"
                        onClick={() => navigate(`/b2b/billing?client=${clientId}&room=${row.roomId}`)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: colors.textPrimary,
                          fontWeight: 600,
                          fontSize: '14px',
                          cursor: 'pointer',
                        }}
                      >
                        {formatMoney(row.total)}
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {zeroRooms.length > 0 ? (
          <div style={{ marginTop: '16px', borderTop: `1px solid ${colors.border}`, paddingTop: '14px' }}>
            <button
              type="button"
              className="font-body"
              aria-expanded={showZeroRooms}
              onClick={() => setShowZeroRooms((current) => !current)}
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textPrimary,
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              {showZeroRooms ? 'Hide' : 'Show'} rooms with no laundry ({zeroRooms.length})
            </button>
            {showZeroRooms ? (
              <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {zeroRooms.map((room) => (
                  <Chip key={room.roomId} style={{ opacity: 0.7 }}>
                    {room.roomNumber} · -
                  </Chip>
                ))}
              </div>
            ) : null}
            <p className="font-body" style={{ margin: '10px 0 0', fontSize: '12px', color: colors.textMuted }}>
              Rooms with no laundry are excluded from the invoice total.
            </p>
          </div>
        ) : null}

        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ minWidth: '260px' }}>
            <div
              className="font-body"
              style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: colors.textMuted }}
            >
              <span>Subtotal</span>
              <span>{formatMoney(grandTotal)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                borderTop: `2px solid ${colors.textPrimary}`,
                marginTop: '10px',
                paddingTop: '10px',
              }}
            >
              <span className="font-body" style={{ fontSize: '13px', fontWeight: 600 }}>
                Total Payable
              </span>
              <span style={{ fontSize: '26px', fontWeight: 700 }}>{formatMoney(grandTotal)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* ── the review gate (design: "Client invoice review") ───────── */}
      {confirming && cycle ? (
        <Modal
          title="Generate Client Invoice"
          onClose={() => setConfirming(false)}
          width="480px"
          footer={
            <>
              <PillButton variant="secondary" onClick={() => setConfirming(false)}>
                Cancel
              </PillButton>
              <PillButton onClick={() => void runGeneration()}>
                <FileText size={14} /> Confirm
              </PillButton>
            </>
          }
        >
          <p
            className="font-body"
            style={{ margin: '0 0 16px', fontSize: '12px', lineHeight: 1.6, color: colors.textSecondary }}
          >
            You are generating one consolidated invoice for the client. Room data remains supporting
            detail and will not create separate room invoices.
          </p>

          <div
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              background: colors.surface,
              padding: '0 14px',
            }}
          >
            {[
              ['Client', cycle.client.name],
              ['Billing cycle', cycle.cycle.label],
              ['Rooms billed', String(roomsBilled)],
              ['Current total', formatMoney(grandTotal)],
            ].map(([label, value], index) => (
              <div
                key={label}
                className="font-body"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '11px 0',
                  borderTop: index === 0 ? 'none' : `1px solid ${colors.border}`,
                }}
              >
                <span style={{ fontSize: '12px', color: colors.textSecondary }}>{label}</span>
                <span style={{ fontSize: '13px', fontWeight: 700 }}>{value}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(214,185,123,0.5)',
              background: 'rgba(214,185,123,0.14)',
            }}
          >
            <FileText size={14} style={{ flexShrink: 0, marginTop: '2px' }} color={colors.accentText} />
            <span className="font-body" style={{ fontSize: '11px', lineHeight: 1.5, color: colors.textSecondary }}>
              This review prevents an accidental invoice. Confirm only when the client cycle is
              ready - generating it closes {cycle.cycle.label} and opens the next.
            </span>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
